import { Checkbox, Grid, GridColumn, GridRow, Select, type SelectOptionType } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { BookingsTable } from 'components/Bookings/tables/BookingsTable';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { getAdminRoleTranslation } from 'utils/adminRoles';
import { BookingsModal } from '../../../components/Bookings/modals/BookingsModal';
import { BpCard } from '../../../components/BpCard/BpCard';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import styles from './SettingsOverviewSubpage.module.scss';
import type { LicensedProductTypes } from './types';

const UpdateBildungsplattformsInSettingsMutationDocument = graphql(`
  mutation UpdateBildungsplattformsInSettings($update: BildungsplattformUpdateInput!, $where: BildungsplattformWhere!) {
    updateBildungsplattforms(update: $update, where: $where) {
      bildungsplattforms {
        uuid
        __typename
        allowStudentDirectMessages
        currentSchoolyear
      }
    }
  }
`);

const DeleteBildungsplattformsMutationDocument = graphql(`
  mutation DeleteBildungsplattformsMutation($organizationUuid: ID!) {
    deleteBildungsplattforms(where: { organization: { uuid: $organizationUuid } }) {
      nodesDeleted
    }
  }
`);

const DeleteBBBMutationDocument = graphql(`
  mutation DeleteBBBMutation($organizationUuid: ID!) {
    deleteBigBlueButtons(where: { organization: { uuid: $organizationUuid } }) {
      nodesDeleted
    }
  }
`);

const SettingsOverviewSubpageQueryDocument = graphql(`
  query SettingsOverviewSubpage($organizationUuid: ID!) {
    adminProfiles(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      displayName
      organizationRoles
    }
    ...BookingsTableFields
  }
`);

export const SettingsOverviewSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }] = useQuery({
    query: SettingsOverviewSubpageQueryDocument,
    variables: {
      organizationUuid: organizationUuid,
    },
  });

  const permissions = usePermissionChecker();

  const [, updateBildungsplattform] = useMutation(UpdateBildungsplattformsInSettingsMutationDocument);
  const [, deleteBildungsplattform] = useMutation(DeleteBildungsplattformsMutationDocument);
  const [, deleteBigBlueButton] = useMutation(DeleteBBBMutationDocument);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<LicensedProductTypes | null>(null);

  const {
    organizationProducts,
    schoolyears,
    currentSchoolYear,
    setCurrentSchoolYear,
    allowStudentDirectMessages,
    setAllowStudentDirectMessages,
  } = useContext(OrganizationConfigContext);

  const bildungsplattform = organizationProducts?.find((p) => p.__typename === 'Bildungsplattform');

  const schoolyearSelectOptions = useCreateSelectOptions(schoolyears, 'uuid', 'name');

  const handleSelectSchoolyear = async (ev: SelectOptionType) => {
    const selectedSchoolyear = schoolyears?.find((s) => s.uuid === ev.value);
    if (selectedSchoolyear !== undefined) {
      if (bildungsplattform) {
        const resp = await updateBildungsplattform({
          update: {
            currentSchoolyear: selectedSchoolyear.uuid,
          },
          where: { uuid: bildungsplattform.uuid },
        });
        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          setCurrentSchoolYear(selectedSchoolyear);
          showSuccessToast(t('common.saved'));
        }
      }
    }
  };

  const handleAllowStudentDirectMessages = async (value: boolean) => {
    if (bildungsplattform) {
      const resp = await updateBildungsplattform({
        update: {
          allowStudentDirectMessages: value,
        },
        where: { uuid: bildungsplattform.uuid },
      });
      if (resp.error) {
        showErrorToast(resp.error);
      } else {
        setAllowStudentDirectMessages(value);
        showSuccessToast(t('common.saved'));
      }
    }
  };

  const handleEdit = (licensedProduct: LicensedProductTypes) => {
    setModalType(licensedProduct);
    setModalOpen(true);
  };
  const handleDelete = async (licensedProduct: LicensedProductTypes) => {
    switch (licensedProduct) {
      case 'Bildungsplattform':
        await deleteBildungsplattform({ organizationUuid: organizationUuid });
        break;
      case 'BigBlueButton':
        await deleteBigBlueButton({ organizationUuid: organizationUuid });
        break;
      default:
      /** */
    }
  };

  return (
    <BpSubpage className={styles['settings-overview']}>
      <Grid>
        <GridRow mobileGap='var(--grid-column-gap)' className={styles.top}>
          <GridColumn width={9}>
            <BpCard header={{ headline: t('common.administration') }}>
              <div className={styles['admins-list']}>
                {data?.adminProfiles.map((p) => {
                  return (
                    <div key={p.uuid} className={styles.admin}>
                      <div className={styles.name}>{p.displayName}</div>
                      <div className={styles.roles}>
                        {p.organizationRoles.map(
                          (role) =>
                            role &&
                            getAdminRoleTranslation(role) && <div key={role}>{getAdminRoleTranslation(role)}</div>,
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </BpCard>
          </GridColumn>
          <GridColumn width={3}>
            <BpCard header={{ headline: t('settings.title') }} showOverflow>
              <Select
                options={schoolyearSelectOptions}
                onChange={async (e) => {
                  await handleSelectSchoolyear(e as unknown as SelectOptionType);
                }}
                defaultValue={schoolyearSelectOptions.find((opt) => currentSchoolYear?.uuid === (opt.value as string))}
                name={'currentSchoolyear'}
                disabled={!permissions?.isOrganizationAdmin(pimAuthClaims.getOrganization())}
                label={t('common.schoolyear')}
              />
              <Checkbox
                name={'allowStudentDirectMessages-direct'}
                checked={allowStudentDirectMessages}
                onChange={async () => {
                  await handleAllowStudentDirectMessages(!allowStudentDirectMessages);
                }}
                disabled={!permissions?.isOrganizationAdmin(pimAuthClaims.getOrganization())}
                label={t('institution.allowStudentDirectMessages')}
              />
            </BpCard>
          </GridColumn>
        </GridRow>
        <GridRow>
          <BpCard noPadding header={{ headline: t('institution.actualBookings') }}>
            <BookingsTable onEdit={handleEdit} onDelete={handleDelete} query={data} />
          </BpCard>
        </GridRow>
      </Grid>

      {modalType && (
        <BookingsModal
          bookingType={modalType}
          isOpen={modalOpen}
          onClose={() => {
            setModalType(null);
            setModalOpen(false);
          }}
        />
      )}
    </BpSubpage>
  );
};
